/**
 * The bare-noun follow-up (D37; extracted from `pending-completion` at the
 * 500-line cap, D84; re-founded on the parser's own reading at D133) — a noun
 * nobody asked about describes its thing, then volunteers the "what would you
 * like done with it?" question, parked for the SURFACE only (§3.3/D84: the
 * record keeps what happened; the offer lives where offers live).
 *
 * @remarks
 * Until D133 this module found bare nouns by re-parsing failed input as
 * `inspect <input>` and REPLACING the parser's diagnosis — a patch over "the
 * engine committing before it finishes reading" (D123 §2.1), and one that
 * could not follow every path the diagnosis took: the which-entity answer
 * path never saw it, so a verbless tie's answer described without the
 * follow-up (D123's open half), and a pack with no `inspect` had no reading.
 *
 * Now the PARSER reads the whole clause as a name before it will blame the
 * name on its first word (`rescue-bare-noun.ts`), and the command it emits
 * says so: `rescuedAs: 'bare_noun'`. This module only asks one question of
 * an executed input — was the player's LAST act naming a thing? — and
 * volunteers the follow-up when it was. Both paths through `runPlayerInput`
 * reach it, so the answer to a which-one question earns the same offer the
 * unambiguous form always did.
 *
 * @packageDocumentation
 */

import type { RuntimeState } from '@orpheus/content-schema';

import { type EntityRef, parseCommand, type ParsedCommand, type ParseOutcome } from '@orpheus/parser-engine';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { bumpResponseUsage, speakSlot } from '../defaults/default-responses.js';
import { deriveEntityCatalog } from '../entities/derive-entity-catalog.js';
import { describeEntityRef } from '../entities/describe-entity-ref.js';
import { resolveNarrativeLines } from '../narrative/conditional-text.js';
import { buildParseContext } from './parse-context.js';

/**
 * The thing an input's LEADING clause names, when the parser read that clause
 * as a bare noun — or `undefined` when it did not (a command, a tie, a
 * failure, a vocabulary with no `inspect`: the rung declines for all of them).
 *
 * @remarks
 * The answer-slot boundary (D64, R28) asks this exact question — "does the
 * input lead with the name of a thing?" — so it has exactly one definition,
 * and it is the parser's. The LEADING clause, because an answer may be the
 * head of a chain ("crowbar and go north", D91): the name answers, and the
 * rest is the player moving on.
 *
 * @param outcome - The parse of the whole input
 * @returns The named thing's reference, or `undefined`
 */
export const leadingBareNounRef = (outcome: ParseOutcome): EntityRef | undefined => {
	// A chain whose TAIL fails still led with whatever its prefix parsed (R21):
	// "crowbar and take the zeppelin" answers with the crowbar and then fails
	// on its own, which is the answer's fate being its clause's (D91).
	const leadingCommand = outcome.type === 'parsed' ? outcome.commands[0] : outcome.parsedPrefix[0];

	return leadingCommand?.rescuedAs === 'bare_noun' ? leadingCommand.directObjectRef : undefined;
};

/**
 * {@link leadingBareNounRef}, for callers holding text rather than a parse —
 * read as a NOUN PHRASE first (`readAsNounPhrase`), because every caller is
 * an answer slot, where the question already supplied the verb and "pry bar"
 * is the bar's name (D64).
 *
 * @param bundle - The loaded bundle
 * @param state - The state BEFORE the input
 * @param rawInput - Exactly what the player typed
 * @returns The named thing's reference, or `undefined`
 */
export const bareNounObjectRef = (bundle: EngineGameBundle, state: RuntimeState, rawInput: string): EntityRef | undefined =>
	leadingBareNounRef(
		parseCommand(rawInput, buildParseContext(bundle, deriveEntityCatalog(bundle, state), { options: { readAsNounPhrase: true }})),
	);

/** The player's last act, when it was naming a thing: what they named, and the words they used. */
export interface LastNaming {
	/** The naming clause, folded — a bare verb answers BEFORE it ("open" + "locker"). */
	readonly clauseText: string;
	readonly namedRef: EntityRef;
}

/**
 * The thing the player's last act named, when that act was a bare noun.
 *
 * @remarks
 * The LAST executed command, not any: "locker, north" describes the locker
 * and then leaves — the player moved on, and an offer about the locker would
 * be the engine talking about something the player has finished with.
 *
 * @param executedCommands - The input's executed commands, in order
 * @returns The naming, or `undefined` when the input did not end on a name
 */
export const lastNamingOf = (executedCommands: readonly ParsedCommand[]): LastNaming | undefined => {
	const lastCommand = executedCommands.at(-1);

	return lastCommand?.rescuedAs === 'bare_noun' && lastCommand.directObjectRef !== undefined
		? { clauseText: lastCommand.clauseText, namedRef: lastCommand.directObjectRef }
		: undefined;
};

/**
 * Volunteer the D37 follow-up onto an executed state: speak the prompt into
 * the pool ledger and park it as a verb-slot completion question.
 *
 * @remarks
 * §3.3 (D84, Calder's verdict, ruled by Mike): the follow-up parks WITHOUT
 * printing. R10's print-the-ask law is for questions the player's own input
 * CREATED — a tie, a missing instrument. This one is volunteered: nothing was
 * ambiguous and no argument was missing, so a printed line here is the engine
 * talking about itself, and answering it re-ran the describe so the paragraph
 * printed twice. The record keeps what happened (the player named a thing;
 * the world described it); the SURFACE shows the offer — that is what the
 * surface is for. The pool still advances (D58): the line was spoken, just
 * not into the record.
 *
 * @param bundle - The loaded bundle
 * @param state - The state AFTER the describe executed
 * @param naming - What the player named, and the words they used
 * @returns The state with the offer parked
 */
export const volunteerBareNounFollowUp = (bundle: EngineGameBundle, state: RuntimeState, naming: LastNaming): RuntimeState => {
	const { clauseText, namedRef } = naming;
	const spokenPrompt = speakSlot(bundle, state, 'bareNounPrompt', { itemName: describedThingName(bundle, namedRef) });

	return {
		...state,
		session: {
			...state.session,
			// The follow-up is itself a question: a bare VERB answers it, and
			// the answer belongs BEFORE the noun ("open" + "kettle"). D79: the
			// park carries its own spoken text and context.
			pendingQuestion: {
				askedAtInputOrdinal: state.session.commandHistory.length,
				clauseText,
				joiner: '',
				kind: 'completion',
				prefixesAnswer: true,
				/*
				 * UNREACHABLE `?? ''`. `resolveNarrativeLines` maps 1:1 over its input
				 * and this call passes exactly one line, so `[0]` is always present —
				 * the same shape, and the same reason, as the park in
				 * `pending-question.ts`. The default is what
				 * `noUncheckedIndexedAccess` requires of an index access, not a case
				 * the runtime can produce.
				 *
				 * Verified 2026-08-23: a throwing default leaves the whole repo suite
				 * green.
				 */
				/* v8 ignore next */
				questionText: resolveNarrativeLines([spokenPrompt.line], bundle, state)[0]?.text ?? '',
				roomId: state.progression.activeRoomId,
				// D225 (E4): the offer was never printed, so leaving its room
				// closes it without a word — the record cannot withdraw aloud a
				// question it never heard asked.
				volunteered: true,
			},
			responseUsage: bumpResponseUsage(state.session.responseUsage, [spokenPrompt.slot]),
		},
	};
};

/**
 * What the follow-up should CALL the thing just described.
 *
 * @remarks
 * D41 made the room referable, so a bare `here` describes the room — and the
 * prompt has to be able to name it. Looking only in `itemsById` left the room
 * case nameless and shipped "The it, then. What would you like done with it?"
 * to players.
 *
 * The room does NOT answer with its authored name, for the same reason the
 * D41 refusal does not: the prompt frames its subject ("The {itemName}, then")
 * and a room called "The Hall" would read "The The Hall, then". `room itself`
 * is the phrasing D41 already established for exactly this position, so both
 * surfaces say it the same way.
 *
 * D232: the TOTAL namer (`describeEntityRef`) answers for every kind the
 * catalog can hold — an item by its name, an actor by its name, the room by
 * its D41 framing (`room itself`) — so a bare `woman` is "The woman in the sump,
 * then." and not a throw from an item lookup handed a body.
 *
 * @param bundle - The loaded bundle
 * @param inspectedRef - The reference the bare-noun reading resolved
 * @returns What the follow-up should call it
 */
const describedThingName = (bundle: EngineGameBundle, inspectedRef: EntityRef): string => describeEntityRef(bundle, inspectedRef);
